import { useState, type Dispatch, type SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { CVEducation } from '../../../lib/cv-mock-data';
import { normalizeEducationYear, validateEducation } from './cv-form-validation';

function newDraftId(): string {
  return `local-${Math.random().toString(36).slice(2, 11)}`;
}

export function emptyEducationItem(): CVEducation {
  return { id: newDraftId(), degree: '', school: '', year: '', description: '' };
}

interface UseEducationReviewQueueParams {
  t: TFunction;
  setItems: Dispatch<SetStateAction<CVEducation[]>>;
  /** Titles for the review sheet: adding a new entry vs editing an existing one. */
  labels: { addTitle: string; editTitle: string };
  /**
   * Web layers extra modal state (the Spark description preview) that must be
   * cleared whenever the review sheet opens, advances, or closes. Native passes
   * nothing.
   */
  onModalReset?: () => void;
}

/**
 * Owns the education review-sheet + multi-entry queue state shared by the native
 * and web EditEducationForm. When one "Describe with AI" prompt yields several
 * entries, they are reviewed one sheet at a time (`reviewQueue` holds the ones
 * still to review; `reviewTotal` drives the "n / m" progress in the title). Every
 * entry passes the same `validateEducation` gate via `commit` before it lands in
 * the list, so an entry with a missing school/year can never be saved.
 */
export function useEducationReviewQueue({
  t,
  setItems,
  labels,
  onModalReset,
}: UseEducationReviewQueueParams) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<CVEducation>(emptyEducationItem());
  const [showErrors, setShowErrors] = useState(false);
  const [reviewQueue, setReviewQueue] = useState<CVEducation[]>([]);
  const [reviewTotal, setReviewTotal] = useState(0);

  const errors = validateEducation(draft, t);
  const hasErrors = Object.keys(errors).length > 0;

  const resetQueue = () => {
    setReviewQueue([]);
    setReviewTotal(0);
  };

  // The AI deliberately leaves required fields empty rather than inventing a
  // degree/school the prompt never stated. Highlight those gaps inline the moment
  // a drafted entry opens, instead of only after a failed save.
  const showGapsFor = (entry: CVEducation) =>
    setShowErrors(Object.keys(validateEducation(entry, t)).length > 0);

  const beginReview = (drafts: CVEducation[]) => {
    const [first, ...rest] = drafts;
    setReviewQueue(rest);
    setReviewTotal(drafts.length);
    setDraft(first);
    setEditingIndex(-1);
    showGapsFor(first);
    onModalReset?.();
  };

  const openAdd = () => {
    setDraft(emptyEducationItem());
    setEditingIndex(-1);
    setShowErrors(false);
    resetQueue();
    onModalReset?.();
  };

  const openEdit = (item: CVEducation, index: number) => {
    setDraft({ ...item });
    setEditingIndex(index);
    setShowErrors(false);
    resetQueue();
    onModalReset?.();
  };

  const close = () => {
    setEditingIndex(null);
    setShowErrors(false);
    resetQueue();
    onModalReset?.();
  };

  const commit = () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    // Store the picker's own year shape, so an entry imported as "Mar-2015" is
    // written back as "Mar 2015".
    const normalizedDraft = { ...draft, year: normalizeEducationYear(draft.year) };
    if (editingIndex === -1) setItems((prev) => [...prev, normalizedDraft]);
    else if (editingIndex !== null)
      setItems((prev) => {
        const next = [...prev];
        next[editingIndex] = normalizedDraft;
        return next;
      });
    if (reviewQueue.length > 0) {
      // Advance to the next drafted entry, keeping the review sheet open.
      const [next, ...rest] = reviewQueue;
      setReviewQueue(rest);
      setDraft(next);
      setEditingIndex(-1);
      showGapsFor(next);
      onModalReset?.();
      return;
    }
    setEditingIndex(null);
    setShowErrors(false);
    setReviewTotal(0);
  };

  const reviewPosition = reviewTotal > 1 ? reviewTotal - reviewQueue.length : 0;
  const baseTitle = editingIndex === -1 ? labels.addTitle : labels.editTitle;
  const modalTitle =
    reviewPosition > 0
      ? `${baseTitle} (${reviewPosition}/${reviewTotal})`
      : baseTitle;

  return {
    editingIndex,
    draft,
    setDraft,
    showErrors,
    errors,
    hasErrors,
    modalTitle,
    beginReview,
    openAdd,
    openEdit,
    close,
    commit,
  };
}
